/**
 * Os ícones do conteúdo das páginas de Game Design — e quem decide qual desenho cabe.
 *
 * <p><b>O que existe é reusado.</b> Atributos vêm de {@link AtributoIcone}, perícias de
 * {@link PericiaIcone}, os derivados (HP, SP, peso, sono) de {@link DerivadoIcone}, e o
 * resto — lugares, ações, portadores — dos desenhos do menu ({@link IconeDoMenu}). Só as
 * condições que nenhum deles cobre ganham desenho aqui ({@link CondicaoIcone}): Fome,
 * Frio, Veneno, Atordoado e companhia.
 *
 * <p><b>Por nome, não por posição.</b> As tabelas são editáveis — o time renomeia,
 * reordena e acrescenta linha —, então o ícone de uma linha é resolvido pelo <i>texto</i>
 * da primeira célula: "Espada" acha a perícia, "FOR" acha o atributo, "Viajar (troca de
 * área)" acha a ação. Texto que nenhum resolvedor conhece fica sem ícone, e isso é o
 * certo: um desenho genérico ali diria "o sistema sabe o que é isto", e não sabe. É
 * também por isso que os resolvedores devolvem {@code null} em vez de cair no círculo
 * padrão do {@code PericiaIcone}.
 *
 * <p>Comparação sem acento nem caixa: "Caçar" e "cacar" são a mesma chave — quem
 * digita numa célula não deve precisar acertar o acento para ganhar o ícone.
 */
import type { ReactNode } from 'react';

import { IconeDoMenu } from '../../shell/IconeDoMenu';
import { PericiaIcone } from '../mundo/PericiaIcone';
import { ATRIBUTOS } from './atributos';
import { AtributoIcone, DerivadoIcone } from './IconesDaFicha';
import { TODAS_AS_PERICIAS } from './pericias';

/** O tamanho em que todos saem nas tabelas e nos títulos — um só, para a página não virar mostruário. */
const TAMANHO = 18;

/** "Caçar (Percepção)" → "cacar (percepcao)": sem acento, sem caixa, sem espaço nas pontas. */
export const normalizar = (texto: string) =>
  texto
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** A primeira palavra do texto — o que identifica uma ação ("Viajar (troca de área)"). */
const primeiraPalavra = (texto: string) => normalizar(texto).split(/[\s(·,\/]+/)[0] ?? '';

export const iconeDoMenu = (icone: string, titulo?: string): ReactNode => (
  <IconeDoMenu icone={icone} tamanho={TAMANHO} titulo={titulo} />
);

export const iconeDerivado = (nome: string, titulo?: string): ReactNode => (
  <DerivadoIcone nome={nome} tamanho={TAMANHO} titulo={titulo} />
);

// ------------------------------------------------------------------ atributos

/** Pela sigla ("FOR") ou pelo nome ("Força"). */
export function iconeDeAtributo(siglaOuNome: string): ReactNode | null {
  const chave = normalizar(siglaOuNome);
  const a = ATRIBUTOS.find((x) => normalizar(x.sigla) === chave || normalizar(x.nome) === chave);
  return a ? <AtributoIcone nome={a.sigla} cor={a.cor} tamanho={TAMANHO} titulo={a.nome} /> : null;
}

// ------------------------------------------------------------------- perícias

/*
 * As perícias que não estão no catálogo com desenho próprio, mas que a página de
 * Personagem lista — as OUTRAS da mesa e as que só o épico/GDD trazem. Cada uma pega
 * emprestado o desenho mais próximo; as que não têm nenhum honesto (Joalheria, Trabalho
 * em Madeira) ficam de fora de propósito.
 */
const PERICIAS_EMPRESTADAS: Record<string, () => ReactNode> = {
  medicina: () => iconeDoMenu('pocao', 'Medicina'),
  cozinhar: () => <CondicaoIcone nome="descanso" titulo="Cozinhar" />,
  acampar: () => <CondicaoIcone nome="descanso" titulo="Acampar" />,
  memorizar: () => iconeDeAtributo('INT'),
  estudar: () => iconeDeAtributo('INT'),
  escrever: () => iconeDoMenu('documento', 'Escrever'),
  viajar: () => iconeDoMenu('mapa', 'Viajar'),
  cacar: () => iconeDoMenu('alvo', 'Caçar'),
  'armas exoticas': () => <PericiaIcone pericia="combate" tamanho={TAMANHO} titulo="Armas Exóticas" />,
  'escudo arcano': () => <PericiaIcone pericia="arcana" tamanho={TAMANHO} titulo="Escudo Arcano" />,
};

/** Pelo id ("maca") ou pelo rótulo ("Contusão") do catálogo; depois pelas emprestadas. */
export function iconeDePericia(rotuloOuId: string): ReactNode | null {
  const chave = normalizar(rotuloOuId);
  const p = TODAS_AS_PERICIAS.find(
    (x) => normalizar(x.id) === chave || normalizar(x.rotulo) === chave
  );
  if (p) return <PericiaIcone pericia={p.id} cor={p.cor} tamanho={TAMANHO} titulo={p.rotulo} />;
  const emprestada = PERICIAS_EMPRESTADAS[chave];
  return emprestada ? emprestada() : null;
}

// ------------------------------------------------------------- tipos de cena

/** Os doze tipos do código, mais o que o GDD chama de outro nome. */
const TIPOS_DE_CENA: Record<string, string> = {
  start: 'sol',
  regiao: 'ilha',
  area: 'alvo',
  praca: 'rosacea',
  local: 'losango',
  npc: 'personagem',
  mob: 'felino',
  rec: 'saco',
  evento: 'losango-duplo',
  merc: 'bau',
  jornal: 'documento',
  rank: 'barras',
  combate: 'espada',
  coleta: 'saco',
  criacao: 'bigorna',
};

export function iconeDeTipoDeCena(codigo: string): ReactNode | null {
  const icone = TIPOS_DE_CENA[primeiraPalavra(codigo)];
  return icone ? iconeDoMenu(icone, codigo) : null;
}

// ---------------------------------------------------------------------- ações

/** Pela primeira palavra: "Coletar · Caçar · Minerar" é coletar, "Viajar (troca de área)" é viajar. */
const ACOES: Record<string, () => ReactNode> = {
  viajar: () => iconeDoMenu('mapa', 'Viajar'),
  explorar: () => iconeDoMenu('olho', 'Explorar'),
  deslocar: () => iconeDoMenu('fluxo', 'Deslocar'),
  coletar: () => iconeDoMenu('saco', 'Coletar'),
  cacar: () => iconeDoMenu('alvo', 'Caçar'),
  minerar: () => iconeDoMenu('picareta', 'Minerar'),
  criar: () => iconeDoMenu('bigorna', 'Criar'),
  alquimia: () => iconeDePericia('alquimia'),
  forjar: () => iconeDePericia('forja'),
  couraria: () => iconeDePericia('couraria'),
  descansar: () => <CondicaoIcone nome="descanso" titulo="Descansar" />,
  cochilar: () => iconeDerivado('qualidade-do-sono', 'Cochilar'),
  medicar: () => iconeDoMenu('pocao', 'Medicar'),
  cozinhar: () => <CondicaoIcone nome="descanso" titulo="Cozinhar" />,
  estudar: () => iconeDeAtributo('INT'),
  memorizar: () => iconeDeAtributo('INT'),
  escrever: () => iconeDoMenu('documento', 'Escrever'),
  dormir: () => iconeDerivado('qualidade-do-sono', 'Dormir'),
  comer: () => <CondicaoIcone nome="fome" titulo="Comer" />,
  usar: () => iconeDoMenu('pocao', 'Usar item'),
  conversar: () => iconeDoMenu('pessoas', 'Conversar'),
  negociar: () => iconeDoMenu('pessoas', 'Negociar'),
  interagir: () => iconeDoMenu('pessoas', 'Interagir'),
  aprender: () => iconeDoMenu('documento', 'Aprender'),
  combater: () => iconeDoMenu('espada', 'Combater'),
  combate: () => iconeDoMenu('espada', 'Combate'),
  ataque: () => iconeDoMenu('espada', 'Ataque'),
  atacar: () => iconeDoMenu('espada', 'Atacar'),
  fugir: () => iconeDoMenu('fluxo', 'Fugir'),
  fuga: () => iconeDoMenu('mapa', 'Fuga'),
  perseguir: () => iconeDoMenu('alvo', 'Perseguir'),
  encontro: () => iconeDoMenu('pessoas', 'Encontro'),
  resolucao: () => iconeDoMenu('fluxo', 'Resolução'),
  // Os quatro tipos de ataque e as defesas — as linhas das tabelas da página Combate.
  golpear: () => iconeDePericia('machado'),
  perfurar: () => iconeDePericia('haste'),
  disparar: () => iconeDePericia('arco'),
  arremessar: () => iconeDoMenu('alvo', 'Arremessar'),
  esquivar: () => iconeDePericia('esquiva'),
  esquiva: () => iconeDePericia('esquiva'),
  bloquear: () => iconeDePericia('bloqueio'),
  bloqueio: () => iconeDePericia('bloqueio'),
  levantar: () => iconeDoMenu('personagem', 'Levantar'),
  derrubar: () => iconeDoMenu('espada', 'Derrubar'),
  // O catálogo da planilha de Ações de Combate — resolvido pela primeira palavra do nome.
  avancar: () => iconeDoMenu('fluxo', 'Avançar'),
  recuar: () => iconeDoMenu('fluxo', 'Recuar'),
  aproximar: () => iconeDoMenu('fluxo', 'Aproximar'),
  evadir: () => iconeDoMenu('fluxo', 'Evadir'),
  escapar: () => iconeDoMenu('fluxo', 'Escapar'),
  avanco: () => iconeDoMenu('fluxo', 'Avanço Rápido'),
  empurrar: () => iconeDoMenu('fluxo', 'Empurrar'),
  agarrar: () => <CondicaoIcone nome="agarrado" titulo="Agarrar" />,
  defender: () => iconeDerivado('defesa', 'Defender'),
  preparar: () => <CondicaoIcone nome="energia" titulo="Preparar" />,
  trocar: () => iconeDoMenu('saco', 'Trocar equipamento'),
  saque: () => iconeDoMenu('saco', 'Saque Rápido'),
  mirar: () => iconeDoMenu('alvo', 'Mirar'),
  fintar: () => iconeDoMenu('olho', 'Fintar'),
  intimidar: () => iconeDoMenu('pessoas', 'Intimidar'),
  golpe: () => iconeDePericia('machado'),
  golpes: () => iconeDePericia('machado'),
  estocada: () => iconeDePericia('haste'),
  estocadas: () => iconeDePericia('haste'),
  estocar: () => iconeDePericia('haste'),
  disparo: () => iconeDePericia('arco'),
  disparos: () => iconeDePericia('arco'),
  rajada: () => iconeDoMenu('espada', 'Rajada'),
  investida: () => iconeDoMenu('espada', 'Investida'),
  rolar: () => iconeDePericia('esquiva'),
  encontrao: () => iconeDoMenu('espada', 'Encontrão'),
  bote: () => iconeDoMenu('felino', 'Bote'),
  pancada: () => iconeDoMenu('felino', 'Pancada'),
  mordida: () => iconeDoMenu('felino', 'Mordida'),
  garrada: () => iconeDoMenu('felino', 'Garrada'),
  rabada: () => iconeDoMenu('felino', 'Rabada'),
  chifrada: () => iconeDoMenu('felino', 'Chifrada'),
  bicada: () => iconeDoMenu('felino', 'Bicada'),
  picada: () => iconeDoMenu('felino', 'Picada'),
  atropelar: () => iconeDoMenu('felino', 'Atropelar'),
  aura: () => iconeDePericia('arcana'),
  misseis: () => iconeDePericia('magiaOfensiva'),
  bola: () => iconeDePericia('magiaOfensiva'),
  aparatar: () => iconeDePericia('arcana'),
};

export function iconeDeAcao(nome: string): ReactNode | null {
  const acao = ACOES[primeiraPalavra(nome)];
  return acao ? acao() : null;
}

// --------------------------------------------------------------- portadores

/** Os lugares onde um item ou uma receita mora: mochila, cinto, grimório, memória. */
const PORTADORES: Record<string, () => ReactNode> = {
  mochila: () => iconeDoMenu('bau', 'Mochila'),
  cinto: () => iconeDoMenu('saco', 'Cinto'),
  grimorio: () => iconeDoMenu('documento', 'Grimório'),
  'porta-pergaminhos': () => iconeDoMenu('transcricao', 'Porta-Pergaminhos'),
  pergaminho: () => iconeDoMenu('carta', 'Pergaminho'),
  memoria: () => iconeDeAtributo('INT'),
};

export function iconeDePortador(nome: string): ReactNode | null {
  const portador = PORTADORES[primeiraPalavra(nome)];
  return portador ? portador() : null;
}

// --------------------------------------------------------------- condições

/**
 * O ícone de uma condição, pela chave do quadro em "Condições do Personagem".
 *
 * <p>As primárias reusam os derivados da Ficha (HP, SP, peso, sono) — é o mesmo conceito,
 * e um segundo desenho para o mesmo HP negaria a ligação. As demais são desenhos daqui.
 */
const CONDICOES: Record<string, () => ReactNode> = {
  vigor: () => iconeDerivado('hp', 'Vigor'),
  hp: () => iconeDerivado('hp', 'Vigor'),
  concentracao: () => iconeDerivado('sp', 'Concentração'),
  sp: () => iconeDerivado('sp', 'Concentração'),
  carga: () => iconeDerivado('peso-carregado', 'Carga'),
  'escala-da-dormida': () => iconeDerivado('qualidade-do-sono', 'Escala da dormida'),
  dormida: () => iconeDerivado('qualidade-do-sono', 'Dormida'),
  energia: () => <CondicaoIcone nome="energia" titulo="Energia" />,
  descanso: () => <CondicaoIcone nome="descanso" titulo="Descanso" />,
  envenenado: () => <CondicaoIcone nome="veneno" titulo="Envenenado" />,
  fome: () => <CondicaoIcone nome="fome" titulo="Fome" />,
  frio: () => <CondicaoIcone nome="frio" titulo="Frio" />,
  cansaco: () => <CondicaoIcone nome="cansaco" titulo="Cansaço" />,
  sono: () => iconeDerivado('qualidade-do-sono', 'Sono'),
  atordoado: () => <CondicaoIcone nome="atordoado" titulo="Atordoado" />,
  caido: () => <CondicaoIcone nome="caido" titulo="Caído" />,
  paralisado: () => <CondicaoIcone nome="paralisado" titulo="Paralisado" />,
  furioso: () => <CondicaoIcone nome="furioso" titulo="Furioso" />,
  agarrado: () => <CondicaoIcone nome="agarrado" titulo="Agarrado" />,
  amedrontado: () => <CondicaoIcone nome="amedrontado" titulo="Amedrontado" />,
  impedido: () => <CondicaoIcone nome="impedido" titulo="Impedido / Preso" />,
  sangrando: () => <CondicaoIcone nome="sangrando" titulo="Sangrando" />,
  fraqueza: () => <CondicaoIcone nome="fraqueza" titulo="Fraqueza" />,
  lento: () => <CondicaoIcone nome="lento" titulo="Lento" />,
  apressado: () => <CondicaoIcone nome="apressado" titulo="Apressado" />,
  vigilante: () => <CondicaoIcone nome="vigilante" titulo="Vigilante" />,
  regenerando: () => <CondicaoIcone nome="regenerando" titulo="Regenerando" />,
};

export function iconeDeCondicao(chaveOuNome: string): ReactNode | null {
  const condicao = CONDICOES[primeiraPalavra(chaveOuNome)];
  return condicao ? condicao() : null;
}

/** As chaves que {@link iconeDeCondicao} conhece — para o teste prender a cobertura. */
export const CONDICOES_COM_ICONE = Object.keys(CONDICOES);

/**
 * Os desenhos de condição que nenhum outro conjunto tinha.
 *
 * <p>Mesma gramática do {@link IconeDoMenu}: traço em {@code currentColor}, sem
 * preenchimento, ponta redonda, silhueta que sobrevive a 18px. Cada um é uma forma só —
 * a gota, o floco, o raio —, porque a 18px detalhe é ruído.
 */
export function CondicaoIcone({
  nome,
  tamanho = TAMANHO,
  titulo,
}: {
  nome: string;
  tamanho?: number;
  titulo?: string;
}) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={titulo ?? nome}
    >
      {titulo && <title>{titulo}</title>}
      {desenhoDeCondicao(nome)}
    </svg>
  );
}

function desenhoDeCondicao(nome: string) {
  switch (nome) {
    // O raio: o orçamento de esforço do dia.
    case 'energia':
      return <path d="M13 2 4 14h7l-1 8 9-13h-7l1-7z" />;
    // A fogueira: parar para recuperar.
    case 'descanso':
      return (
        <>
          <path d="M12 3c2.5 3 5 5.2 5 9a5 5 0 0 1-10 0c0-2 .8-3.2 2-4.4 0 1.8.8 3 2 3.4 1-2 1-5 1-8z" />
          <path d="M5 21h14" />
        </>
      );
    // A gota com bolhas: o veneno.
    case 'veneno':
      return (
        <>
          <path d="M12 3s6 6.6 6 11a6 6 0 0 1-12 0c0-4.4 6-11 6-11z" />
          <circle cx="10" cy="14" r="1" />
          <circle cx="14" cy="11.5" r="0.8" />
        </>
      );
    // Garfo e faca: a fome.
    case 'fome':
      return (
        <>
          <path d="M7 3v18M4.5 3v5a2.5 2.5 0 0 0 5 0V3" />
          <path d="M17.5 3c-2.5 4-2.5 8 0 10.5V21" />
        </>
      );
    // O floco: o frio.
    case 'frio':
      return (
        <>
          <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9" />
          <path d="M12 3l-2 2M12 3l2 2M12 21l-2-2M12 21l2-2" />
        </>
      );
    // Os três zês, encolhendo: o cansaço.
    case 'cansaco':
      return (
        <>
          <path d="M4 6h6l-6 7h6" />
          <path d="M13 11h4l-4 5h4" />
          <path d="M16 18h3l-3 3h3" />
        </>
      );
    // As estrelas em volta da cabeça: atordoado.
    case 'atordoado':
      return (
        <>
          <circle cx="12" cy="15" r="5" />
          <path d="M6 4l.7 1.5L8.3 6l-1.6.6L6 8l-.7-1.4L3.7 6l1.6-.5z" />
          <path d="M17.5 3l.9 1.9 2 .6-2 .7-.9 1.8-.9-1.8-2-.7 2-.6z" />
        </>
      );
    // A seta que desce até o chão: caído.
    case 'caido':
      return (
        <>
          <path d="M12 3v13M6.5 10.5 12 16l5.5-5.5" />
          <path d="M4 21h16" />
        </>
      );
    // Dois elos: paralisado.
    case 'paralisado':
      return (
        <>
          <rect x="2.5" y="9" width="9" height="6" rx="3" />
          <rect x="12.5" y="9" width="9" height="6" rx="3" />
        </>
      );
    // A garra fechando: agarrado.
    case 'agarrado':
      return (
        <>
          <path d="M5 4c-1.5 3 -1.5 6 1 9" />
          <path d="M12 3c-2 3.5 -2 7 0 10.5" />
          <path d="M19 4c1.5 3 1.5 6 -1 9" />
          <path d="M5.5 20c2-3 11-3 13 0" />
        </>
      );
    // O ponto de exclamação tremendo: amedrontado.
    case 'amedrontado':
      return (
        <>
          <path d="M12 4v9" />
          <circle cx="12" cy="18.5" r="1.2" />
          <path d="M6.5 6.5c-1 2-1 4 0 6M17.5 6.5c1 2 1 4 0 6" />
        </>
      );
    // A rede: impedido / preso.
    case 'impedido':
      return (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 3.5v17M3.5 12h17M6 6l12 12M18 6 6 18" />
        </>
      );
    // A chama dupla: furioso.
    case 'furioso':
      return (
        <>
          <path d="M9 21c-3 0-5-2.2-5-5 0-2.6 1.6-4 2.6-5.6.4 1.6 1.2 2.4 2.4 2.6C8 10 8.6 6.4 12 3c0 3.4 2 5 3.4 7.2.8-.6 1.2-1.6 1.4-2.8C18.4 9 20 11 20 14c0 4-3 7-7 7z" />
        </>
      );
    // A gota: sangrando.
    case 'sangrando':
      return <path d="M12 3s6 6.6 6 11a6 6 0 0 1-12 0c0-4.4 6-11 6-11z" />;
    // A seta para baixo sobre a barra: fraqueza.
    case 'fraqueza':
      return (
        <>
          <path d="M12 3v11M7.5 9.5 12 14l4.5-4.5" />
          <path d="M6 19h12" />
        </>
      );
    // O caracol: lento.
    case 'lento':
      return (
        <>
          <circle cx="9" cy="13" r="4.5" />
          <path d="M9 13a2 2 0 0 1 2-2" />
          <path d="M13.5 13c0-4 2-6.5 4.5-8M18 5l1.5-1.5M18 5l2 .5" />
          <path d="M3 20h17" />
        </>
      );
    // As linhas de vento: apressado.
    case 'apressado':
      return (
        <>
          <path d="M3 8h11a2.5 2.5 0 1 0-2.5-2.5" />
          <path d="M3 12h15a2.5 2.5 0 1 1-2.5 2.5" />
          <path d="M3 16h8" />
        </>
      );
    // O olho: vigilante.
    case 'vigilante':
      return (
        <>
          <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
        </>
      );
    // A cruz no círculo: regenerando.
    case 'regenerando':
      return (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 8v8M8 12h8" />
        </>
      );
    default:
      return <circle cx="12" cy="12" r="7" />;
  }
}
